import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tabs,
  Select,
  RelativeTime,
  DatePicker,
} from '@chargify/custom-ui-components';
import { FilterBox, FilterBoxProps } from '../FilterBox/FilterBox';
import { CalendarContainer } from './DateFilter.styles';
import { FilterDate } from '../../../../machines/billingHistoryData/types';
import { DateFilterType } from '../../../../machines/billingFilters';

const tabs = [
  { label: 'time_range', id: 'timeRange' },
  { label: 'relative', id: 'relative' },
];

const options = [
  { label: 'due_date', value: 'due_date' },
  { label: 'issue_date', value: 'issue_date' },
  { label: 'paid_date', value: 'paid_date' },
];

export interface DateFilterProps extends Omit<FilterBoxProps, 'children'> {
  values?: DateFilterType;
  onChange?: (filter: DateFilterType) => void;
  onTabChange: (tab: string) => void;
  portalContainer: RefObject<HTMLElement>;
}

export const DateFilter = ({
  onChange,
  values,
  onClick,
  header,
  isExpanded,
  portalContainer,
  onTabChange,
}: DateFilterProps) => {
  const { t } = useTranslation('billing-history');
  const { dateField, type } = values;
  const startDate = type === 'timeRange' && values?.startDate;
  const endDate = type === 'timeRange' && values?.endDate;

  const translatedTabs: { label: string; id: string }[] = tabs.map(
    ({ label, id }) => ({
      label: t(label),
      id,
    })
  );

  const translatedOptions = options.map(({ label, value }) => ({
    label: t(label),
    value,
  }));

  return (
    <FilterBox onClick={onClick} isExpanded={isExpanded} header={header}>
      <Select
        options={translatedOptions}
        onSelect={(option) =>
          onChange({ ...values, dateField: option.value as FilterDate })
        }
        selectedValue={dateField}
        portalContainer={portalContainer}
        selectSize="small"
      />
      <Tabs
        tabs={translatedTabs}
        onClick={onTabChange}
        activeTabId={values.type}
      />
      {values.type === 'relative' && (
        <RelativeTime
          unit={values.unit}
          value={values.value}
          portalContainer={portalContainer}
          onChange={(timeframe) => onChange({ ...values, ...timeframe })}
        />
      )}
      {values.type === 'timeRange' && (
        <CalendarContainer gridColumnGap={2} gridTemplateColumns={'1fr 1fr'}>
          <DatePicker
            portalContainer={portalContainer}
            calendarAlignment="right"
            maxDate={endDate}
            onChange={(date) =>
              onChange({
                ...values,
                startDate: date,
              })
            }
            translations={{ placeholder: t('from') }}
            value={values?.startDate}
          />
          <DatePicker
            portalContainer={portalContainer}
            calendarAlignment="right"
            minDate={startDate}
            onChange={(date) =>
              onChange({
                ...values,
                endDate: date,
              })
            }
            translations={{ placeholder: t('to') }}
            value={values?.endDate}
          />
        </CalendarContainer>
      )}
    </FilterBox>
  );
};
